import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, CheckCircle, Users, Zap, TrendingUp, DollarSign, Shield, Clock } from 'lucide-react';
import Navigation from './Navigation';
import Footer from './Footer';
import ParticleBackground from './ParticleBackground';
import ScrollingRibbon from './ScrollingRibbon';
import VibeLink from './VibeLink';

const TalentLanding = () => {
  const [displayText, setDisplayText] = useState('');
  const fullText = 'Be Part Of The Human+AI Revolution';
  const [showContent, setShowContent] = useState(false);
  const [loadingComplete, setLoadingComplete] = useState(false);

  useEffect(() => {
    let currentIndex = 0;
    const typingInterval = setInterval(() => {
      if (currentIndex <= fullText.length) {
        setDisplayText(fullText.slice(0, currentIndex));
        currentIndex++;
      } else {
        clearInterval(typingInterval);
        setShowContent(true);
        // Trigger loading complete after typing
        setTimeout(() => {
          setLoadingComplete(true);
        }, 300);
      }
    }, 50); // 50ms per character

    return () => clearInterval(typingInterval);
  }, []);

  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
  }, []);
  const valueProps = [
    {
      icon: <DollarSign className="w-8 h-8 text-orange-500" />,
      title: "No Fees, Ever. Keep All You Earn",
      description: "Keep 100% of what you earn. No platform fees, no commissions, no fine print."
    },
    {
      icon: <Shield className="w-8 h-8 text-orange-500" />,
      title: "We Handle Logistics",
      description: "No invoices. No billing headaches. Payments go straight to you."
    },
    {
      icon: <Zap className="w-8 h-8 text-orange-500" />,
      title: "The Human+AI Spotlight",
      description: "HireX8 is the only platform built exclusively for Human+AI talent - showcasing you to agencies, enterprises, and founders who value innovation."
    },
    {
      icon: <TrendingUp className="w-8 h-8 text-orange-500" />,
      title: "Grow with Launch Labs",
      description: "Build and develop your no-coding and vibe coding skills by joining Launch Labs, a 9,000 member community of vibe coders building for the future."
    }
  ];

  const howItWorks = [
    {
      step: "1",
      title: "Apply",
      description: "Submit your background, tools, and areas of expertise."
    },
    {
      step: "2",
      title: "Get Verified",
      description: "Our team confirms your Human+AI skillset."
    },
    {
      step: "3",
      title: "Get Matched",
      description: "AI-powered matching connects you with clients who need your expertise."
    },
    {
      step: "4",
      title: "Start Earning",
      description: "Work per sprint or per week - paid fast and directly."
    }
  ];

  const faqs = [
    {
      question: "Do I pay anything to join?",
      answer: "No - HireX8 never charges talent fees or takes a percentage of your work."
    },
    {
      question: "Who can join?",
      answer: "Anyone who uses AI or no-code tools as part of their professional craft."
    },
     {
      question: "I don't use AI, what can I do?",
      answer: "Join Launch Labs, our partner community of over 9,000 AI and vibe coders learning to build the future."
    },
    {
      question: "When do I get paid?",
      answer: "Weekly for hourly work, or per sprint for project-based work."
    },
    {
      question: "Do I need to be full-time?",
      answer: "No - you set your own availability and project preferences."
    },
    {
      question: "How do I get paid?",
      answer: "Payments are processed via Stripe Connect. If your country does not support Stripe Connect, your payments can be processed by Gusto. Just choose your preferred payment method during registration."
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>Join HireX8 - Human+AI Talent Platform</title>
        <meta name="description" content="Join the world's first talent platform built for Human+AI professionals. Showcase your skills, accelerate your growth, and keep 100% of what you earn." />
        <meta property="og:title" content="Join HireX8 - Human+AI Talent Platform" />
        <meta property="og:description" content="Join the world's first talent platform built for Human+AI professionals." />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="https://hirex8.com/talent" />
      </Helmet>

      {/* Navigation - fade in after loading */}
      <div className={`transition-opacity duration-700 ${loadingComplete ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
        <Navigation variant="talent" />
      </div>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center bg-black overflow-hidden">
        {/* Particle background - fade in after typing */}
        <div className={`absolute inset-0 transition-opacity duration-700 ${showContent ? 'opacity-100' : 'opacity-0'}`}>
          <ParticleBackground />
        </div>
        
        <div className="max-w-7xl mx-auto px-6 relative z-10 py-20">
          <div className="text-center max-w-4xl mx-auto space-y-6">
            {/* Badge - fade in after typing */}
            <div className={`flex items-center justify-center gap-2 mb-4 transition-opacity duration-700 ${showContent ? 'opacity-100' : 'opacity-0'}`}>
              <div className="w-2 h-2 rounded-full bg-orange-500"></div>
              <span className="text-sm text-white font-medium uppercase tracking-wider">The Workforce Of Tomorrow, Today</span>
            </div>

            {/* Main heading - always visible */}
            <h1 className="text-5xl md:text-7xl font-bold text-white leading-tight mb-6 min-h-[180px] md:min-h-[160px]">
              {displayText}
              <span className="animate-pulse">|</span>
            </h1>

            {/* Description - fade in after typing */}
            <p className={`text-xl md:text-2xl text-gray-300 leading-relaxed mb-8 transition-all duration-700 ${showContent ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
              Join the world's first talent platform built for Human+AI professionals - where your skills shine, 
              your growth accelerates, and you keep 100% of what you earn.
            </p>

            {/* Button - fade in after typing */}
            <div className={`mt-[30px] transition-all duration-700 delay-300 ${showContent ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
              <VibeLink path="/register" params="?type=talent" className="bg-orange-600 hover:bg-orange-700 text-white text-lg px-10 py-4 rounded-lg inline-flex items-center gap-3 group transition-colors">
                Apply to Join HireX8
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </VibeLink>
            </div>
          </div>
        </div>
      </section>

      {/* All sections below - fade in after loading completes */}
      <div className={`transition-opacity duration-700 ${loadingComplete ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
        {/* Scrolling Ribbon */}
        <ScrollingRibbon variant="orange" />

      {/* WHY Section - The Mission */}
      <section id="mission" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center space-y-6">
            <div className="flex items-center justify-center gap-2 mb-4">
              <div className="w-2 h-2 rounded-full bg-orange-500"></div>
              <span className="text-sm text-gray-600 font-medium uppercase tracking-wider">THE MISSION</span>
            </div>

            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Why HireX8 Exists
            </h2>

            <div className="text-lg text-gray-600 leading-relaxed space-y-4">
              <p>
                Work is changing. AI is no not replacing people - it's amplifying them.
              </p>
              <p>
                HireX8 was created for the next generation of professionals who blend human creativity with AI-driven precision.
              </p>
              <p>
                We believe the future of work belongs to those who know how to harness these tools.
              </p>
            </div>

            {/* Visual Element */}
            <div className="mt-12 py-8 px-12 bg-gradient-to-r from-orange-50 to-gray-50 rounded-2xl border-2 border-orange-200">
              <div className="text-3xl md:text-4xl font-bold text-gray-900">
                Human <span className="text-orange-500">+</span> AI <span className="text-orange-500">=</span> 8x Potential
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WHAT Section - The Platform */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="flex items-center justify-center gap-2 mb-4">
              <div className="w-2 h-2 rounded-full bg-orange-500"></div>
              <span className="text-sm text-gray-600 font-medium uppercase tracking-wider">THE PLATFORM</span>
            </div>

            <h2 className="text-4xl md:text-5xl font-bold text-gray-900">
              What Makes HireX8 Different
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
            {valueProps.map((prop, index) => (
              <div key={index} className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-300">
                <div className="mb-4">{prop.icon}</div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">{prop.title}</h3>
                <p className="text-gray-600 leading-relaxed">{prop.description}</p>
              </div>
            ))}
          </div>

          <div className="text-center">
            <VibeLink path="/register" params="?type=talent" className="bg-orange-600 hover:bg-orange-700 text-white text-lg px-10 py-4 rounded-lg inline-flex items-center gap-3 group transition-colors">
              Become Part of HireX8
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </VibeLink>
          </div>
        </div>
      </section>

      {/* HOW Section - The Experience */}
      <section id="how-it-works" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="flex items-center justify-center gap-2 mb-4">
              <div className="w-2 h-2 rounded-full bg-orange-500"></div>
              <span className="text-sm text-gray-600 font-medium uppercase tracking-wider">THE EXPERIENCE</span>
            </div>

            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              How It Works
            </h2>
          </div>

          <div className="grid md:grid-cols-4 gap-8 mb-8">
            {howItWorks.map((step, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-orange-500 rounded-full flex items-center justify-center text-white text-2xl font-bold mx-auto mb-4 shadow-lg">
                  {step.step}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{step.title}</h3>
                <p className="text-gray-600">{step.description}</p>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              No bidding wars. No endless proposals. Just real projects, verified clients, and transparent pay.
            </p>
          </div>
        </div>
      </section>

      {/* Launch Labs Partnership */}
      <section id="skill-development" className="py-24 bg-gradient-to-br from-gray-900 to-black text-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <div className="flex items-center justify-center gap-2 mb-4">
              <div className="w-2 h-2 rounded-full bg-orange-500"></div>
              <span className="text-sm text-orange-400 font-medium uppercase tracking-wider">SKILL DEVELOPMENT</span>
            </div>

            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Grow With the Best in the Industry
            </h2>

            <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed mb-8">
              Build and develop your no-coding and vibe coding skills by joining <strong className="text-white">Launch Labs</strong>, 
              a 9,000 member community of vibe coders building for the future.
            </p>

            <p className="text-lg text-gray-400 max-w-3xl mx-auto">
              Learn the latest AI workflows, refine your portfolio, and collaborate with peers on real projects 
              that boost your career trajectory.
            </p>
          </div>

          {/* Visual: Two Box Layout with Launch Labs Logo */}
          <div className="flex flex-col md:flex-row items-center justify-center gap-8 mt-16 mb-12">
            <div className="text-center">
              <div className="w-40 h-40 rounded-2xl flex items-center justify-center mb-3 shadow-2xl p-4" style={{ backgroundColor: '#F8CD61' }}>
                <img 
                  src="/launch-labs-logo.png" 
                  alt="Launch Labs" 
                  className="w-full h-full object-contain"
                />
              </div>
              <p className="text-gray-400 text-lg">Learn & Build</p>
            </div>
            
            <div className="text-orange-500 text-5xl font-bold">→</div>
            
            <div className="text-center">
              <div className="w-40 h-40 bg-white rounded-2xl flex items-center justify-center text-gray-900 text-xl font-bold mb-3 shadow-2xl border-2 border-gray-200">
                HireX8™
              </div>
              <p className="text-gray-400 text-lg">Earn</p>
            </div>
          </div>

          <div className="text-center">
            <a href="https://www.launchlabshq.com" target="_blank" rel="noopener noreferrer">
              <button className="bg-orange-600 hover:bg-orange-700 text-white text-lg px-10 py-4 rounded-lg inline-flex items-center gap-3 group transition-colors">
                Join Launch Labs Today
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </button>
            </a>
          </div>
        </div>
      </section>

      {/* Community Spotlight - Hidden */}
      <section className="hidden py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <div className="flex items-center justify-center gap-2 mb-4">
              <div className="w-2 h-2 rounded-full bg-orange-500"></div>
              <span className="text-sm text-gray-600 font-medium uppercase tracking-wider">COMMUNITY SPOTLIGHT</span>
            </div>

            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Join the Humans Behind the Innovation
            </h2>

            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our talent network includes AI engineers, UX designers, no-code developers, Vibe Coders, 
              SEO strategists, and creative technologists - all united by one goal: to push what's possible 
              with Human+AI collaboration.
            </p>
          </div>

          {/* Grid of faces */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-5xl mx-auto">
            <img src="/team/khalid_f.jpg" alt="Talent" className="w-full h-64 object-cover rounded-2xl shadow-lg hover:shadow-xl transition-shadow" />
            <img src="/team/jamal_j.jpg" alt="Talent" className="w-full h-64 object-cover rounded-2xl shadow-lg hover:shadow-xl transition-shadow" />
            <img src="/team/raj_b.jpg" alt="Talent" className="w-full h-64 object-cover rounded-2xl shadow-lg hover:shadow-xl transition-shadow" />
            <img src="/team/dina_l.jpg" alt="Talent" className="w-full h-64 object-cover rounded-2xl shadow-lg hover:shadow-xl transition-shadow" />
            <img src="/team/jamie_l.jpg" alt="Talent" className="w-full h-64 object-cover rounded-2xl shadow-lg hover:shadow-xl transition-shadow" />
            <img src="/team/anna_p.jpg" alt="Talent" className="w-full h-64 object-cover rounded-2xl shadow-lg hover:shadow-xl transition-shadow" />
            <img src="/team/john_m.jpg" alt="Talent" className="w-full h-64 object-cover rounded-2xl shadow-lg hover:shadow-xl transition-shadow" />
            <div className="w-full h-64 bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl shadow-lg flex items-center justify-center text-white text-4xl font-bold">
              You?
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-12">
            <div className="flex items-center justify-center gap-2 mb-4">
              <div className="w-2 h-2 rounded-full bg-orange-500"></div>
              <span className="text-sm text-gray-600 font-medium uppercase tracking-wider">TRANSPARENCY</span>
            </div>

            <h2 className="text-4xl md:text-5xl font-bold text-gray-900">
              Your Questions, Answered
            </h2>
          </div>

          <div className="space-y-6">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-shadow">
                <h3 className="text-xl font-bold text-gray-900 mb-2 flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-orange-500 flex-shrink-0 mt-1" />
                  {faq.question}
                </h3>
                <p className="text-gray-600 ml-9">{faq.answer}</p>
              </div>
            ))}
          </div>

          {/* Payment Provider Logos */}
          <div className="mt-16 text-center">
            <p className="text-sm text-gray-500 mb-6 uppercase tracking-wider">We Support International Payments Via</p>
            <div className="flex items-center justify-center gap-12 flex-wrap">
              <img 
                src="/stripe-logo.png" 
                alt="Stripe" 
                className="h-10 object-contain"
              />
              <img 
                src="/gusto-logo.png" 
                alt="Gusto" 
                className="h-10 object-contain"
              />
            </div>
          </div>
        </div>
      </section>

        <Footer variant="talent" />
      </div>
    </div>
  );
};

export default React.memo(TalentLanding);
